import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { IvrConfigurationsService } from '@app/core/services/ivr-configurations/ivr-configurations.service';
import { PpiService } from '@app/core/services/ppi/ppi.service';
import { ICampaignFieldData, IPPIFieldValue, IPPIImgFieldValue } from '@app/core/services/url-address-config/url-address-config.types';
import { TranslateService } from '@ngx-translate/core';
import { isArray } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface SelectConfigInputValue {
  system?: string;
  value: string;
  number: string;
  number_internal: number;
}

@Component({
  selector: 'app-select-configuration-input',
  templateUrl: './select-configuration-input.component.html',
  styleUrls: ['./select-configuration-input.component.scss']
})
export class SelectConfigurationInputComponent implements OnInit, OnDestroy {
  @Input() selectedConfigurationID?: number;
  @Input() submitted = false;
  @Input() fieldInfo?: ICampaignFieldData;
  @Output() emitValue = new EventEmitter<SelectConfigInputValue>();

  public selectedConfiguration: number;
  public configurationSelect: UntypedFormGroup;
  public configurationsList: IPPIFieldValue[] = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    private ivrConfigurationsServices: IvrConfigurationsService,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private ppiService: PpiService
  ) {}

  get isPPISelect(): boolean {
    return this.fieldInfo?.type === 'ppi';
  }

  ngOnInit(): void {
    this.configurationSelect = this.fb.group({
      selectedConfiguration: [null, Validators.required]
    });

    const fieldValues = this.fieldInfo?.values || [];

    this.configurationsList = isArray(fieldValues) ? (fieldValues as IPPIFieldValue[]) : [];
    this.selectFirstConfiguration();
    if (this.fieldInfo?.type === 'ppi') {
      this.ppiService.directLinkAddedOrEdited.pipe(takeUntil(this.unsubscribe$)).subscribe((config: any) => {
        if (config) {
          const configToPush = {};
          configToPush[config.id] = config.name;
          this.configurationsList.push(configToPush);
        }
      });
    } else {
      this.ivrConfigurationsServices.numberAddedOrEdited.pipe(takeUntil(this.unsubscribe$)).subscribe((config: any) => {
        if (config) {
          this.configurationsList.push({ id: config.id, number: config.number, number_internal: config.number_internal });
        }
      });
    }
  }

  get form() {
    return this.configurationSelect.controls;
  }

  selectFirstConfiguration(): void {
    if (this.fieldInfo?.config_values) {
      let currentConfig = this.configurationsList.find(
        config => (config.id ? config.id : parseInt(Object.keys(config)[0], 10)) === parseInt(this.fieldInfo?.config_values ?? '', 10)
      );
      if (!currentConfig) {
        currentConfig = this.configurationsList[0];
      }
      this.configurationSelect?.get('selectedConfiguration')?.patchValue(
        currentConfig.number
          ? `${currentConfig.number}
          ${this.translate.instant('view_translations.internal_shortcut')}
          ${currentConfig.number_internal}`
          : `${Object.values(currentConfig)[0]}`
      );
      this.getValue(currentConfig);
    } else {
      this.configurationSelect?.get('selectedConfiguration')?.patchValue(
        this.configurationsList[0].number
          ? `${this.configurationsList[0].number}
          ${this.translate.instant('view_translations.internal_shortcut')}
          ${this.configurationsList[0].number_internal}`
          : Object.values(this.configurationsList[0])[0]
      );
      this.getValue(this.configurationsList[0]);
    }
  }

  changeChevronUp(): void {
    const elem = document.getElementsByClassName('ng-arrow-wrapper').item(0)!.classList.add('ng-select-open');
  }
  changeChevronDown(): void {
    const elem = document.getElementsByClassName('ng-arrow-wrapper').item(0)!.classList.remove('ng-select-open');
  }

  getValue(config: IPPIFieldValue): void {
    if (this.fieldInfo?.system_name) {
      this.emitValue.emit({
        system: this.fieldInfo.system_name,
        value: (config.id as string) || (Object.keys(config)[0] as string),
        number: config.number as string,
        number_internal: Number(config.number_internal)
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
